import { SVGElementSelection } from "./types"

/**
 * Checks if an element is a group that only contains a single element (should be skipped)
 * Ignores overlay elements when counting children
 */
const isSingleElementWrapper = (element: SVGElement): boolean => {
  if (element.tagName !== "g") return false

  const children = Array.from(element.children).filter(
    (child) => child instanceof SVGElement && !child.hasAttribute("data-tabb-overlay"),
  )
  return children.length === 1
}

/**
 * Builds a path from the clicked element to the root, filtering out wrapper groups
 * Returns array from OUTERMOST to INNERMOST (reversed from DOM traversal)
 */
const buildSelectionPath = (target: SVGElement): SVGElement[] => {
  const rawPath: SVGElement[] = []
  let current: Element | null = target

  while (current && current.tagName !== "svg") {
    if (current instanceof SVGElement && !current.hasAttribute("data-tabb-overlay")) {
      rawPath.push(current)
    }
    current = current.parentElement
  }

  const filteredPath: SVGElement[] = []

  for (let i = rawPath.length - 1; i >= 0; i--) {
    const element = rawPath[i]

    if (isSingleElementWrapper(element)) {
      continue
    }

    filteredPath.push(element)
  }

  return filteredPath
}

/**
 * Finds the appropriate element to select based on the click target and current selection
 */
export const findSelectableElement = (
  target: SVGElement,
  currentSelection: SVGElementSelection | null,
): SVGElementSelection | null => {
  const path = buildSelectionPath(target)
  if (path.length === 0) return null

  if (!currentSelection) {
    return {
      element: path[0],
      path,
      depth: 0,
    }
  }

  if (currentSelection.element === path[currentSelection.depth]) {
    if (currentSelection.depth < path.length - 1) {
      const nextDepth = currentSelection.depth + 1
      return {
        element: path[nextDepth],
        path,
        depth: nextDepth,
      }
    }
  }

  const currentPath = currentSelection.path
  const currentDepth = currentSelection.depth

  if (currentDepth > 0 && path.length > currentDepth) {
    const currentParent = currentPath[currentDepth - 1]
    const newParent = path[currentDepth - 1]

    if (currentParent === newParent) {
      return {
        element: path[currentDepth],
        path,
        depth: currentDepth,
      }
    }
  }

  return {
    element: path[0],
    path,
    depth: 0,
  }
}

/**
 * Assigns or removes a table ID to/from an SVG element
 */
export const assignTableToElement = (element: SVGElement, tableId: string | null) => {
  if (tableId) {
    element.setAttribute("data-tabb-table", tableId)
  } else {
    element.removeAttribute("data-tabb-table")
  }
}

/**
 * Validates if a string is a valid SVG
 */
export const isValidSvg = (content: string): boolean => {
  try {
    const parser = new DOMParser()
    const doc = parser.parseFromString(content, "image/svg+xml")
    const parserError = doc.querySelector("parsererror" as any)

    if (parserError) return false

    const svg = doc.querySelector("svg")
    return svg !== null
  } catch {
    return false
  }
}
